<template>
  <div
    class="pi__img"
    :class="{
      'is-loading': isUploadProcessing
        || (frameSpec && !showUploadedImage && !isBundle) || isLoading,
      'is-error': (frameSpec && customerUploadActive && !canPrintArtwork) || isCanvasOnly,
    }"
  >
    <div
      v-if="(frameSpec && customerUploadActive && !canPrintArtwork) || isCanvasOnly"
      class="product-item__image--error"
    />
    <div
      v-if="(frameSpec && customerUploadActive && !canPrintArtwork) || isCanvasOnly"
      class="product-item__image--message"
    >
      <p class="product-item__image--message-text">
        {{ isCanvasOnly ? 'This frame style is only available for canvas art.' : errorUploadMessage }}
      </p>
    </div>
    <div
      v-if="badge?.copy"
      class="pi__product-badge"
      :style="{ backgroundColor: badge?.background_color || ''}"
    >
      <p :style="{ color: badge?.text_color || '' }">
        {{ badge.copy }}
      </p>
    </div>
    <a
      v-if="!isQuickview"
      :href="productUrl"
      :title="productUrl"
      class="pi__link"
      @mouseenter="onImageHover($event)"
    >
      <div
        v-if="(frameSpec && !isBundle)"
        ref="customUpload"
        :data-fs-url="frameSpecUrl"
        :data-can-print-artwork="canPrintArtwork"
        :data-has-edits="hasFrameSpecEdits"
        class="product-item__image--upload pi__image pi__image--upload js-uploaded-image"
        :class="{'is-visible' : showUploadedImage}"
        >
          <MaskedRenderer
            v-if="frameSpec && hasMaskedRendering"
            :frameSpec="frameSpec"
            class="content masked-renderer"
          />
          <RenderSvgFrame
            v-else
            :frameSpec="frameSpec"
            :needsMouldingReactivity="false"
            class="content single-frame-preview-svg"
          />

      </div>

      <ResponsiveImage
        v-if="!showUploadedImage"
        :image="src"
        :imageAlt="parseImageAlt(alt)"
        wrapperClass="product-item__image pi__image"
        :class="{
          'pi__image--static':
            parseImages(productImages).length > 1 && !disableCarousel
        }"
        :enableTransition="true"
        :hoverImage="hoveredImage"
        :isHovered="isHovered"
        imageClass="js-plp-image"
        modifier="product-item"
      />
      <div
        v-if="parseImages(productImages).length > 1 && !disableCarousel"
        class="pi__image-carousel-wrapper"
      >
        <div class="pi__image-carousel js-product-item-carousel">
          <ResponsiveImage
            v-for="image in parseImages(productImages)"
            :key="image.id"
            :image="image.src"
            :imageAlt="parseImageAlt(image.alt)"
            wrapperClass="product-item__image
              pi__image-carousel-cell js-product-item-carousel-cell"
            imageClass="js-plp-image"
            modifier="product-item"
          />
        </div>
      </div>
    </a>

    <div
      v-else
      class="pi__link"
    >
      <div
        v-if="(frameSpec && !isBundle)"
        ref="customUpload"
        :data-fs-url="frameSpecUrl"
        :data-can-print-artwork="canPrintArtwork"
        :data-has-edits="hasFrameSpecEdits"
        class="product-item__image--upload pi__image pi__image--upload js-uploaded-image"
        :class="{'is-visible' : showUploadedImage}"
      >
        <MaskedRenderer
          v-if="frameSpec && hasMaskedRendering"
          data-test="masked-renderer"
          :frameSpec="frameSpec"
          class="content masked-renderer"
        />
        <RenderSvgFrame
          v-else
          data-test="single-frame-preview-svg"
          :frameSpec="frameSpec"
          :needsMouldingReactivity="false"
          class="content single-frame-preview-svg"
        />
      </div>
      <ResponsiveImage
        v-if="!showUploadedImage"
        :image="src"
        :imageAlt="parseImageAlt(alt)"
        wrapperClass="product-item__image pi__image"
        :class="{
          'pi__image--static':
            parseImages(productImages).length > 1 && !disableCarousel
        }"
        :enableTransition="true"
        :hoverImage="hoveredImage"
        :isHovered="isHovered"
        imageClass="js-plp-image"
        modifier="product-item"
      />
      <div
        v-if="parseImages(productImages).length > 1 && !disableCarousel"
        class="pi__image-carousel-wrapper"
        @mouseenter="hover(true)"
        @mouseleave="hover(false)"
      >
        <div
          ref="carouselRef"
          class="pi__image-carousel js-product-item-carousel"
        >
          <ResponsiveImage
            v-for="image in parseImages(productImages)"
            :key="image.id"
            :image="image.src"
            :imageAlt="parseImageAlt(image.alt)"
            wrapperClass="product-item__image
              pi__image-carousel-cell js-product-item-carousel-cell"
            imageClass="js-plp-image"
            modifier="product-item"
          />
        </div>
      </div>
    </div>
    <div
      v-if="compareEnabled && isCollectionPage"
      class="pi__heart"
    >
      <button
        v-if="type !== 'Gallery Wall Consultation' && type !== 'Gift Card'"
        class="pi__heart-btn js-product-heart btn-icon"
        type="button"
        tabindex="0"
      >
        <span class="screenreader">Favorite Icon</span>
        <span class="pi__heart-icon icon icon-default icon--heart-icon" />
        <span class="pi__heart-icon icon icon-active icon--heart-icon-dark" />
      </button>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  toRefs,
  onMounted,
  onUpdated,
  watchEffect,
  watch,
  computed
} from "vue";
import Flickity from "flickity";
// Components
import RenderSvgFrame from '@framebridge/toolbox/components/RenderSvg/RenderSvgFrame.vue';
import MaskedRenderer from '@framebridge/toolbox/MaskedRenderer';
import ResponsiveImage from '../common/responsive-image/ResponsiveImage';

const props = defineProps({
  activeColor: {
    type: Object,
    required: false,
    default: () => ({})
  },
  isUploadProcessing: {
    type: Boolean,
    required: false,
    default: false
  },
  badge: {
    type: Object,
    required: false,
    default: null
  },
  errorUploadMessage: {
    type: String,
    required: false,
    default: null
  },
  customerUploadActive: {
    type: Boolean,
    required: false,
    default: false
  },
  canPrintArtwork: {
    type: Boolean,
    required: false,
    default: false
  },
  hasMaskedRendering: {
    type: Boolean,
    required: false,
    default: false
  },
  frameSpec: {
    type: Object,
    required: false,
    default: null
  },
  frameSpecUrl: {
    type: String,
    required: false,
    default: null
  },
  isBundle: {
    type: Boolean,
    required: false,
    default: false
  },
  isQuickview: {
    type: Boolean,
    required: false,
    default: false
  },
  productImages: {
    type: Array,
    required: false,
    default: () => []
  },
  url: {
    type: String,
    required: false,
    default: null
  },
  src: {
    type: String,
    required: false,
    default: null
  },
  alt: {
    type: String,
    required: false,
    default: null
  },
  type: {
    type: String,
    required: false,
    default: null
  },
  isCollectionPage: {
    type: Boolean,
    required: false,
    default: false
  },
  isLoading: {
    type: Boolean,
    required: true,
    default: false
  },
  isHovered: {
    type: Boolean,
    required: false,
    default: false
  },
  hoveredImage: {
    type: Object,
    required: false,
    default: () => ({})
  },
  disableCarousel: {
    type: Boolean,
    required: false,
    default: false
  },
  onImageHover: {
    type: Function,
    required: false,
  },
  parentProduct: {
    type: Object,
    required: false,
    default: null
  },
  parentProductVariant: {
    type: [Number, String],
    required: false,
    default: null
  }
});

const {
  activeColor,
  canPrintArtwork, frameSpec,
  isBundle
} = toRefs(props);
const showUploadedImage = ref(false);
const customUpload = ref(null);
const interval = ref(null);
const carouselRef = ref(null);
const compareEnabled = ref(SDG.Data.collectionsCompareEnabled);
// True when artwork was sized via the framing-flow size step — signals
// quickview CTA should route to /review-your-frame instead of /edit.
const hasFrameSpecEdits = computed(() => !!frameSpec.value?.artworks?.[0]?.edits);

const isCanvasOnly = computed(() => {
  if (!frameSpec.value) return false;
  const errors = frameSpec.value.price?.errors;
  if (!errors) return false;
  return Object.values(errors).flat().some((msg) => msg.includes("does not support paper"));
});

const productUrl = computed(() => {
  if (props.parentProduct?.handle && props.parentProductVariant) {
    return `/products/${props.parentProduct?.handle}?variant=${props.parentProductVariant}`;
  }
  return props.url && !props.isQuickview ? props.url : null;
});

// Methods
/**
 * Parse Image Alt
 * @param {Object} altText
 */
const parseImageAlt = (altText) => {
  if (!altText) {return altText;}
  const imageAltSplit = altText.includes("|") ? altText.split("|") : null;
  if (!imageAltSplit) {return altText;}
  const [, splitEnd] = imageAltSplit;
  return splitEnd || altText;
};

/**
 * Parse Images
 * @param {Object} images
 */
const parseImages = (images) => {
  const parsedImages = images?.filter((image) => {
    if (!image.alt) {return false;}
    const imageAltSplit = image.alt.includes("|") ? image.alt.split("|") : null;
    if (!imageAltSplit) {return false;}
    const [splitStart] = imageAltSplit;
    const splitStartColor = splitStart?.includes("/")
      ? splitStart.split("/")[0].toLowerCase().trim()
      : splitStart.toLowerCase().trim();
    const activeColorValue = activeColor.value.color || activeColor.value;
    const formattedSplitStartColor = splitStartColor.replace(/ /g, "_").replace(":", "");
    return (
      activeColorValue === formattedSplitStartColor
        || activeColorValue === splitStartColor
    );
  });
  return parsedImages;
};
const hover = __.debounce((val) => {
  const $carousel = Flickity.data(carouselRef.value) || null;
  if (!$carousel) {return;}

  if (val) {
    interval.value = setInterval(() => {
      $carousel.next();
    }, 1500);
  } else {
    clearInterval(interval.value);
    $carousel.select(0);
  }
}, 50);

const observeSVGs = (el) => {
  const observer = new MutationObserver((mutationsList) => {
    mutationsList.forEach((mutation) => {
      if (mutation.type === "attributes" && mutation.attributeName === "viewBox") {
        showUploadedImage.value = Boolean(frameSpec);
      }
      if (mutation.type === "attributes" && mutation.attributeName === "style") {
        if (el.classList.contains("masked-renderer")) {
          showUploadedImage.value = Boolean(frameSpec);;
        }
      }
    });
  });

  observer.observe(el, {
    attributes: true,
    attributeFilter: ["viewBox", "style"]
  });
};

// Watch for changes to `canPrintArtwork`
watch(
  canPrintArtwork,
  (newCanPrintArtwork, oldCanPrintArtwork) => {
    if (newCanPrintArtwork !== oldCanPrintArtwork) {
      if (!newCanPrintArtwork) {
        // If `canPrintArtwork` is now false, hide the uploaded image
        showUploadedImage.value = false;
      } else {
        // If `canPrintArtwork` is now true, show the uploaded image if we have a frameSpec
        showUploadedImage.value = Boolean(frameSpec);
      }
    }
  },
  { immediate: true } // Run the watcher immediately after the component is mounted
);

// Watch for changes to `frameSpec`
watch(
  frameSpec,
  (newFrameSpec, oldFrameSpec) => {
    if (newFrameSpec !== oldFrameSpec) {
      if (!newFrameSpec) {
        // If `frameSpec` is now null or undefined, hide the uploaded image
        showUploadedImage.value = false;
      } else {
        // If `frameSpec` is now defined, show the uploaded image if we have a frameSpec
        showUploadedImage.value = Boolean(newFrameSpec);
      }
    }
  },
  { immediate: true } // Run the watcher immediately after the component is mounted
);

// lifecycle hooks
onMounted(() => {
  watchEffect(() => {
    if (customUpload.value) {
      const $svgElements = customUpload.value.querySelectorAll("svg") || [];
      const $maskedRenderers = customUpload.value.querySelectorAll(".masked-renderer") || [];
      if ($svgElements?.length > 0) {
        [...$svgElements].forEach((svgElement) => observeSVGs(svgElement));
      }

      if ($maskedRenderers?.length > 0) {
        [...$maskedRenderers].forEach((maskedRenderer) => {
          observeSVGs(maskedRenderer);
        });
      }
    }
  });
});

onUpdated(() => {
  if (customUpload.value) {
    const $svgElements = customUpload.value.querySelectorAll("svg") || [];
    const $maskedRenderers = customUpload.value.querySelectorAll(".masked-renderer") || [];

    if ($svgElements?.length > 0) {
      [...$svgElements].forEach((svgElement) => observeSVGs(svgElement));
    }

    if ($maskedRenderers?.length > 0) {
      [...$maskedRenderers].forEach((maskedRenderer) => {
        observeSVGs(maskedRenderer);
      });
    }
  }
});
</script>
